import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { TableAction, UserRole } from '../types';
import { getAvailableActions, getActionText, calculateTableStatus } from '../utils/tableUtils';

interface TableActionModalProps {
  tableId: number;
  onClose: () => void;
  onServed?: () => void;
}

const TableActionModal: React.FC<TableActionModalProps> = ({ tableId, onClose, onServed }) => {
  const { state, userRole, executeAction } = useApp();
  const [confirmingAction, setConfirmingAction] = useState<TableAction | null>(null);
  
  const table = state.tables.find(t => t.id === tableId);
  
  if (!table) {
    return null;
  }

  const availableActions = getAvailableActions(table, userRole);
  const currentStatus = calculateTableStatus(table);

  const handleActionClick = (action: TableAction) => {
    // Storno-Aktionen benötigen Bestätigung
    if (action === TableAction.CANCEL || action === TableAction.CONFIRM_CANCEL) {
      setConfirmingAction(action);
      return;
    }
    
    // Konfetti bei "Serviert" Action
    if (action === TableAction.MARK_SERVED && onServed) {
      onServed();
    }
    
    executeAction(tableId, action);
    onClose();
  };

  const handleConfirmAction = () => {
    if (confirmingAction) {
      executeAction(tableId, confirmingAction);
      setConfirmingAction(null);
      onClose();
    }
  };

  const getActionButtonClass = (action: TableAction): string => {
    switch (action) {
      case TableAction.START_COOKING:
        return 'action-button action-warning';
      case TableAction.CONFIRM_PIZZA:
        return 'action-button action-primary';
      case TableAction.MARK_READY:
        return 'action-button action-success';
      case TableAction.MARK_SERVED:
        return 'action-button action-success';
      case TableAction.CANCEL:
      case TableAction.CONFIRM_CANCEL:
        return 'action-button action-cancel'; // Spezielle kleinere Klasse für Stornieren
      default:
        return 'action-button action-primary';
    }
  };

  const getActionIcon = (action: TableAction): string => {
    switch (action) {
      case TableAction.START_COOKING:
        return '🍕';
      case TableAction.CONFIRM_PIZZA:
        return '🍕';
      case TableAction.MARK_READY:
        return '✅';
      case TableAction.MARK_SERVED:
        return '🍽️';
      case TableAction.CANCEL:
        return '❌';
      case TableAction.CONFIRM_CANCEL:
        return '✅';
      default:
        return '🔄';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'available':
        return '⚪';
      case 'cooking':
        return '🟠';
      case 'confirmed':
        return '🔵';
      case 'kitchen_ready_pending':
        return '🟠'; // Orange Icon für Pizzaiolo-Aufmerksamkeit
      case 'partially_ready':
        return '🔵';
      case 'ready':
        return '🟢';
      case 'cancelled':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'available':
        return 'Verfügbar';
      case 'cooking':
        return 'In Zubereitung';
      case 'confirmed':
        return 'Bestätigt';
      case 'kitchen_ready_pending':
        return 'Küche fertig - Pizza-Bestätigung erforderlich';
      case 'partially_ready':
        return 'Teilweise bereit zum Servieren';
      case 'ready':
        return 'Bereit zum Servieren';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  // Bestätigungsdialog für Storno
  if (confirmingAction) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <span>⚠️</span>
            <h3 className="modal-title">Bestätigung erforderlich</h3>
          </div>
          
          <div className="modal-content">
            <p>
              Sind Sie sicher, dass Sie "{getActionText(confirmingAction, userRole)}" 
              für Tisch {table.number} ausführen möchten?
            </p>
          </div>
          
          <div className="modal-footer">
            <button 
              className="modal-cancel"
              onClick={() => setConfirmingAction(null)}
            >
              Abbrechen
            </button>
            <button 
              className="action-button action-danger"
              onClick={handleConfirmAction}
            >
              <span>{getActionIcon(confirmingAction)}</span>
              <span>Bestätigen</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span>{getStatusIcon(currentStatus)}</span>
          <h3 className="modal-title">Tisch {table.number}</h3>
        </div>
        
        <div className="modal-content">
          <div className="table-info">
            <p><strong>Status:</strong> {getStatusText(currentStatus)}</p>
            <p><strong>Rolle:</strong> {userRole === UserRole.KITCHEN ? 'Küche 👨‍🍳' : 'Pizzaiolo 🍕'}</p>
            <p><strong>Letzte Änderung:</strong> {new Date(table.lastUpdated).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
            
            {/* Separator zwischen Basis-Info und Status-Historie */}
            <div className="modal-separator"></div>
            
            {/* Timeline mit detaillierten Timestamps - nur bei nicht-freien Tischen */}
            {currentStatus !== 'available' && (
              <div className="action-timeline">
                <h4>📅 Aktions-Verlauf:</h4>
                
                {/* Küche gestartet */}
                {table.actions.kitchenStarted && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">🍕</span>
                    <span className="timeline-text">Pizza angefordert</span>
                    {table.actionTimestamps.kitchenStarted && (
                      <span className="timeline-time">
                        {new Date(table.actionTimestamps.kitchenStarted).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Pizza bestätigt */}
                {table.actions.pizzaConfirmed && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">🍕</span>
                    <span className="timeline-text">Pizzazubereitung gestartet</span>
                    {table.actionTimestamps.pizzaConfirmed && (
                      <span className="timeline-time">
                        {new Date(table.actionTimestamps.pizzaConfirmed).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Küche fertig */}
                {table.actions.kitchenReady && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">👨‍🍳</span>
                    <span className="timeline-text">Küche bereit</span>
                    {table.actionTimestamps.kitchenReady && (
                      <span className="timeline-time">
                        {new Date(table.actionTimestamps.kitchenReady).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Pizza fertig */}
                {table.actions.pizzaReady && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">🍕</span>
                    <span className="timeline-text">Pizza bereit</span>
                    {table.actionTimestamps.pizzaReady && (
                      <span className="timeline-time">
                        {new Date(table.actionTimestamps.pizzaReady).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Storniert */}
                {table.actions.cancelledBy && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">❌</span>
                    <span className="timeline-text">Storniert von: {table.actions.cancelledBy === UserRole.KITCHEN ? 'Küche' : 'Pizzaiolo'}</span>
                    {table.actionTimestamps.cancelled && (
                      <span className="timeline-time">
                        {new Date(table.actionTimestamps.cancelled).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Serviert */}
                {table.actionTimestamps.served && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">✅</span>
                    <span className="timeline-text">Serviert / Zurückgesetzt</span>
                    <span className="timeline-time">
                      {new Date(table.actionTimestamps.served).toLocaleTimeString('de-DE', { 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        second: '2-digit' 
                      })}
                    </span>
                  </div>
                )}
                
                {/* Falls keine Aktionen vorhanden */}
                {!table.actions.kitchenStarted && 
                 !table.actions.pizzaConfirmed && 
                 !table.actions.kitchenReady && 
                 !table.actions.pizzaReady && 
                 !table.actions.cancelledBy &&
                 !table.actionTimestamps.served && (
                  <div className="timeline-entry">
                    <span className="timeline-icon">⏳</span>
                    <span className="timeline-text">Tisch bereit für neue Bestellung</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Separator zwischen Status und Aktionen */}
        <div className="modal-separator"></div>
        
        <div className="modal-actions">
          {availableActions.length > 0 ? (
            availableActions.map(action => (
              <button
                key={action}
                className={getActionButtonClass(action)}
                onClick={() => handleActionClick(action)}
              >
                <span>{getActionIcon(action)}</span>
                <span>{getActionText(action, userRole)}</span>
              </button>
            ))
          ) : (
            <p style={{ textAlign: 'center', color: '#666', margin: '20px 0' }}>
              Keine Aktionen verfügbar
            </p>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableActionModal;